/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { globalErrorType } from "@/context/ErrorContext";
import { extractTechnicalDetails } from "./use-toast-helpers";
import { getDefaultTitle } from "./use-toast-config";
import type { ToastType, ToastOptions } from "./use-toast-types";

type TFunc = (key: string, options?: any) => string;

interface ProcessedMessage {
  userMessage: string;
  technicalDetails: string | null;
}

/**
 * Processes an error object to extract a user message and technical details.
 */
export function processErrorObject(
  t: TFunc,
  error: globalErrorType,
  options?: ToastOptions,
): ProcessedMessage {
  const userMessage = options?.message || t('messages.error.generic');
  const technicalDetails =
    options?.technicalDetails !== undefined
      ? options.technicalDetails
      : extractTechnicalDetails(error);
  return { userMessage, technicalDetails };
}

/**
 * Processes a string message (or non-error object) into a user message.
 */
export function processStringMessage(
  t: TFunc,
  messageOrError: string | globalErrorType,
  type: ToastType,
  options?: ToastOptions,
): ProcessedMessage {
  const userMessage =
    typeof messageOrError === "string"
      ? messageOrError
      : options?.message || getDefaultTitle(type, t);
  const technicalDetails = options?.technicalDetails || null;
  return { userMessage, technicalDetails };
}

/**
 * Returns true when the message argument is an error object for an error toast.
 */
export function isErrorObject(
  type: ToastType,
  messageOrError: string | globalErrorType,
): boolean {
  return type === "error" && typeof messageOrError === "object" && messageOrError !== null;
}

/**
 * Resolves the user message and technical details for any toast input.
 */
export function processMessageOrError(
  t: TFunc,
  type: ToastType,
  messageOrError: string | globalErrorType,
  options?: ToastOptions,
): ProcessedMessage {
  if (isErrorObject(type, messageOrError)) {
    return processErrorObject(t, messageOrError as globalErrorType, options);
  }
  return processStringMessage(t, messageOrError, type, options);
}

/**
 * Gets the toast title from options or falls back to the default for the type.
 */
export function getToastTitle(t: TFunc, type: ToastType, options?: ToastOptions): string {
  return options?.title || getDefaultTitle(type, t);
}
